import type { Ref } from 'vue';

type OptionItem = {
  name: string;
  value: number;
};

type UseReportingModalOptionsArgs = {
  selectedCity: Ref<number | null>;
  selectedMarkets: Ref<number[]>;
};

export const useReportingModalOptions = ({
  selectedCity,
  selectedMarkets,
}: UseReportingModalOptionsArgs) => {
  const $apiFetch = useSanctumClient();

  const allCities = ref<OptionItem[]>([]);
  const markets = ref<OptionItem[]>([]);
  const allMarkets = ref<OptionItem[]>([]);
  const placers = ref<OptionItem[]>([]);
  const cityName = ref<Record<number, string>>({});
  const placerName = ref<Record<number, string>>({});
  const isDataLoaded = ref(false);

  const loadMarkets = async () => {
    if (!selectedCity.value || selectedCity.value <= 0) {
      markets.value = [];
      selectedMarkets.value = [];
      return;
    }

    try {
      const response = await $apiFetch(`/api/cities/${selectedCity.value}/markets`, {
        query: { no_pagination: true },
      });

      markets.value = response.data
        .map((item: any) => ({
          name: item.name,
          value: item.id,
        }))
        .sort((a: OptionItem, b: OptionItem) =>
          a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
        );

      selectedMarkets.value = markets.value.map((market) => market.value);
    } catch (error) {
      console.error('Erreur chargement marchés :', error);
    }
  };

  const loadInitialData = async () => {
    try {
      const citiesResponse = await $apiFetch('/api/cities', {
        query: { no_pagination: true },
      });

      allCities.value = [
        { name: 'Toutes les villes', value: -2 },
        ...citiesResponse.data.map((item: any) => ({
          name: item.name,
          value: item.id,
        })),
      ];

      cityName.value = citiesResponse.data.reduce((acc: Record<number, string>, item: any) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      cityName.value[-2] = 'Toutes_Les_Villes';

      const placersResponse = await $apiFetch('/api/placersNoPagination', {
        params: { include: 'roles' },
      });

      placers.value = placersResponse.data
        .filter((item: any) => item.id > 0 && item.disabled === 0)
        .map((item: any) => ({
          name: `${item.name} - ${item.roles?.map((role: any) => role.name).join(', ') ?? ''}`,
          value: item.id,
        }))
        .sort((a: OptionItem, b: OptionItem) =>
          a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
        );

      placerName.value = placersResponse.data.reduce((acc: Record<number, string>, item: any) => {
        acc[item.id] = item.name;
        return acc;
      }, {});

      const marketsResponse = await $apiFetch('/api/markets', {
        query: { no_pagination: true },
      });

      allMarkets.value = marketsResponse.data
        .filter((item: any) => item.enable === 1)
        .map((item: any) => ({
          name: `${item.name} - ${cityName.value[item.city_id]}`,
          value: item.id,
        }))
        .sort((a: OptionItem, b: OptionItem) =>
          a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
        );

      isDataLoaded.value = true;
    } catch (error) {
      console.error('Erreur chargement données reporting :', error);
    }
  };

  return {
    allCities,
    markets,
    allMarkets,
    placers,
    cityName,
    placerName,
    isDataLoaded,
    loadMarkets,
    loadInitialData,
  };
};
